import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl } from '@constants'
import { base62Encode } from '@src/utils'
import { prepareHeaders, transformResponse } from '@src/reduxjs'
import ms from 'ms'

export let getAbortController = new AbortController()

export const abortGetPhotos = () => {
  getAbortController.abort()
}

const photosApi = createApi({
  reducerPath: 'photosApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Photos'],
  endpoints: (builder) => ({
    get: builder.query({
      query: (params) => {
        const { isStore, album, local, search, face, bib, image, startDay, startTime, startMinute, labels, photographers, page, sort, perPage, options, tags, colors, predictionNames, password } = params

        getAbortController = new AbortController()

        const queryParams = [`online=${!!isStore}`]

        if (album && album.id) {
          queryParams.push(`album=${album.id}`)
        }

        if (local && local.id) {
          queryParams.push(`local=${local.id}`)
        }

        if (search && search.value) {
          queryParams.push(`search=${search.value}`)
        }

        if (bib && bib.value) {
          queryParams.push(`bib=${bib.value}`)
        }

        if (face && face.id) {
          queryParams.push(`face=${face.id}`)
        }

        if (image && image.id) {
          queryParams.push(`image=${image.id}`)
        }

        if (startDay && startDay.value) {
          const hours = (startTime || {}).value || 0
          const minutes = (startMinute || {}).value || 0

          queryParams.push(`start_at=${startDay.value + hours * ms('1h') + minutes * ms('1m')}`)
        }

        if (labels && labels.length) {
          labels.forEach((label, index) => {
            queryParams.push(`labels[${index}]=${label.id}`)
          })
        }

        if (photographers && photographers.length) {
          photographers.forEach((photographer, index) => {
            queryParams.push(`photographers[${index}]=${photographer.id}`)
          })
        }

        if (page) {
          queryParams.push(`page=${page}`)
        }

        if (sort) {
          queryParams.push(`sort=${sort}`)
        }

        if (password) {
          queryParams.push(`password=${base62Encode(password)}`)
        }

        if (perPage) {
          queryParams.push(`per_page=${perPage}`)
        }

        for (const [index, option] of Object.entries(options || [])) {
          queryParams.push(`options[${index}]=${option}`)
        }

        for (const [index, tag] of Object.entries(tags || [])) {
          if (!tag) {
            continue
          }
          queryParams.push(`tags[${index}]=${tag}`)
        }

        for (const [index, color] of Object.entries(colors || [])) {
          queryParams.push(`colors[${index}]=${base62Encode(color)}`)
        }

        for (const [index, predictionName] of Object.entries(predictionNames || [])) {
          queryParams.push(`prediction_names[${index}]=${predictionName}`)
        }

        return { url: queryParams.length ? `photos?${queryParams.join('&')}` : 'photos', signal: getAbortController.signal }
      },
      providesTags: (results) => {
        if (!results || !results.data) {
          return [{ type: 'Photos', id: 'LIST' }]
        }
        return [...results.data.map((i) => ({ type: 'Photos', id: i.id })), { type: 'Photos', id: 'LIST' }]
      },
    }),
    getOne: builder.query({
      query: (photo_id) => ({ url: `photos/${photo_id}`, method: 'GET' }),
      invalidatesTags: (results, error, { id }) => {
        return [{ type: 'Photos', id }]
      },
      transformResponse,
    }),
    update: builder.mutation({
      query: ({ id, thumb_base64, tags, is_gift, is_private, is_favorite, is_hidden }) => ({ url: `photos/${id}`, method: 'PATCH', body: { thumb_base64, tags, is_gift, is_private, is_favorite, is_hidden } }),
      transformResponse,
    }),
    delete: builder.mutation({
      query: ({ id }) => ({ url: `photos/${id}`, method: 'DELETE' }),
      transformResponse,
    }),
  }),
})

export const { useGetQuery, useDeleteMutation, useUpdateMutation, endpoints } = photosApi

export default photosApi
